import React, { useState, useEffect, useCallback } from "react";
import { Badge, Button, Spinner, Alert } from "react-bootstrap";
import WhatsAppService from "services/WhatsAppService";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyInfo,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import CountdownTimer from "jsx/components/CitoPlus/components/CountdownTimer";

// Constantes de WhatsAppStatus (deben coincidir con backend)
const WHATSAPP_STATUS = {
  NOT_EXISTS: "not_exists",
  CREATED: "created",
  WAITING_QR: "qr_needed",
  CONNECTING: "connecting",
  CONNECTED: "open",
  DISCONNECTED: "close",
  ERROR: "error",
};

const WhatsAppQRDashboard = () => {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollingController, setPollingController] = useState(null);
  const [autoCreationAttempted, setAutoCreationAttempted] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [userStoppedPolling, setUserStoppedPolling] = useState(false);

  // localStorage key
  const STORAGE_KEY = "whatsapp_dashboard_stopped";

  // Check localStorage for stopped state on component mount
  useEffect(() => {
    const stoppedState = localStorage.getItem(STORAGE_KEY);
    if (stoppedState === "true") {
      setUserStoppedPolling(true);
    }
  }, []);

  // Expose function globally for clearing localStorage on logout
  useEffect(() => {
    window.clearWhatsAppDashboardState = () => {
      localStorage.removeItem(STORAGE_KEY);
      setUserStoppedPolling(false);
    };
    return () => {
      delete window.clearWhatsAppDashboardState;
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const statusData = await WhatsAppService.getStatus();
      setStatus(statusData);
      const connected = statusData.status === WHATSAPP_STATUS.CONNECTED;
      setIsConnected(connected);

      // If connected, clear any stopped state
      if (connected) {
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
      }

      // Si está cerrado (close/DISCONNECTED), marcar como stopped para evitar auto-reconexión
      // Esto previene que intente reconectar cuando la instancia se cerró por inactividad
      if (statusData.status === WHATSAPP_STATUS.DISCONNECTED) {
        setUserStoppedPolling(true);
        localStorage.setItem(STORAGE_KEY, "true");
        // Detener cualquier polling activo
        if (pollingController) {
          pollingController.stop();
          setPollingController(null);
        }
        setPolling(false);
        setQrCode(null);
        setCountdown(null);
      }
    } catch (error) {
      // If no instance exists, that's okay - user needs to register first
      if (error.response?.status !== 404) {
        console.error("Error fetching WhatsApp status:", error);
      }
    }
  }, [pollingController]);

  const stopStatusPolling = useCallback(() => {
    if (pollingController) {
      pollingController.stop();
      setPollingController(null);
    }
    setPolling(false);
    setCountdown(null);
    setQrCode(null);
    setUserStoppedPolling(true);
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, "true");
  }, [pollingController]);

  const startStatusPolling = useCallback(async () => {
    if (pollingController) {
      pollingController.stop();
    }

    setPolling(true);

    const controller = await WhatsAppService.startStatusPolling({
      onStatusUpdate: (statusData) => {
        setStatus(statusData);
        setIsConnected(statusData.status === WHATSAPP_STATUS.CONNECTED);
      },
      onConnected: (statusData) => {
        setStatus(statusData);
        setIsConnected(true);
        setQrCode(null);
        setPolling(false);
        setPollingController(null);
        setCountdown(null);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        notifySuccess("¡WhatsApp conectado exitosamente!");
      },
      onTimeout: () => {
        setQrCode(null);
        setPolling(false);
        setPollingController(null);
        setCountdown(null);
        setUserStoppedPolling(true);
        localStorage.setItem(STORAGE_KEY, "true");
        notifyInfo(
          "Se detuvo la verificación automática después de 3 minutos. Puedes generar un nuevo QR.",
        );
      },
      onCountdown: (countdownData) => {
        setCountdown(countdownData);
      },
      timeoutMinutes: 3,
      intervalSeconds: 5,
    });

    setPollingController(controller);
  }, [pollingController]);

  const fetchQRCode = useCallback(async () => {
    try {
      setQrLoading(true);
      const qrData = await WhatsAppService.getQRCode();
      if (qrData.qr_code) {
        setQrCode(qrData.qr_code);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        startStatusPolling();
        notifyInfo(
          "Nuevo código QR generado con token actualizado. Escanéalo con tu teléfono para conectar WhatsApp.",
        );
      } else {
        notifyError("No se pudo obtener el código QR. Intenta nuevamente.");
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setQrLoading(false);
    }
  }, [startStatusPolling]);

  const handleCreateInstance = useCallback(async () => {
    try {
      setLoading(true);
      const response = await WhatsAppService.createInstance();

      if (response.qr_code) {
        setQrCode(response.qr_code);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
        startStatusPolling();
        notifySuccess(
          "Instancia creada exitosamente. Escanea el código QR para conectar.",
        );
      } else if (
        response.status === "connected" ||
        response.status === "open"
      ) {
        notifyInfo("La instancia ya está conectada a WhatsApp.");
        setIsConnected(true);
        setUserStoppedPolling(false);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        notifySuccess("Instancia creada. Obteniendo código QR...");
        await fetchQRCode();
      }

      await fetchStatus();
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }, [startStatusPolling, fetchQRCode, fetchStatus]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    // Auto-crear instancia SOLO si:
    // 1. El status es "not_exists" (no hay instancia en CitoPlus)
    // 2. NO estamos en loading
    // 3. NO hemos intentado auto-crear aún
    // 4. NO hay QR code mostrado
    // 5. El usuario NO ha detenido el polling manualmente
    //
    // NO auto-crear si status es "close" - el usuario debe presionar "Generar QR"
    if (
      status !== null &&
      status.status === WHATSAPP_STATUS.NOT_EXISTS &&
      !loading &&
      !autoCreationAttempted &&
      !qrCode &&
      !userStoppedPolling
    ) {
      setAutoCreationAttempted(true);
      handleCreateInstance();
    }
  }, [
    status,
    loading,
    autoCreationAttempted,
    qrCode,
    userStoppedPolling,
    handleCreateInstance,
  ]);

  useEffect(() => {
    // Cleanup polling controller on component unmount
    return () => {
      if (pollingController) {
        pollingController.stop();
      }
    };
  }, [pollingController]);

  const getStatusBadge = () => {
    if (!status) return null;

    const statusMap = {
      [WHATSAPP_STATUS.CONNECTED]: { variant: "success", text: "Conectado" },
      [WHATSAPP_STATUS.CONNECTING]: { variant: "warning", text: "Conectando" },
      [WHATSAPP_STATUS.WAITING_QR]: { variant: "info", text: "QR Requerido" },
      [WHATSAPP_STATUS.DISCONNECTED]: {
        variant: "danger",
        text: "Desconectado",
      },
      [WHATSAPP_STATUS.ERROR]: { variant: "danger", text: "Error" },
    };

    const statusInfo = statusMap[status.status] || {
      variant: "secondary",
      text: status.status,
    };

    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  if (loading && !qrLoading) {
    return (
      <div className="card">
        <div className="card-header border-0 justify-content-center">
          <h4 className="fs-20">WhatsApp QR {getStatusBadge()}</h4>
        </div>
        <div className="card-body text-center" style={{ padding: "20px" }}>
          <SpinnerCitoPlus text="Configurando WhatsApp..." />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header border-0 justify-content-center">
        <h4 className="fs-20">WhatsApp QR {getStatusBadge()}</h4>
      </div>
      <div className="card-body text-center" style={{ padding: "20px" }}>
        {/* STATE 1: WhatsApp is connected - Only show connection info */}
        {isConnected && (
          <div>
            <Alert variant="success" className="mb-3">
              <i className="mdi mdi-check-circle me-2"></i>
              WhatsApp conectado y listo
            </Alert>
            <div className="mb-3">
              <strong>Número:</strong>{" "}
              {status?.data?.phone_number || "No disponible"}
              {status?.data?.whatsapp_name && (
                <>
                  <br />
                  <strong>Nombre:</strong> {status.data.whatsapp_name}
                </>
              )}
            </div>
            <Alert variant="info" className="text-start">
              <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                <strong>📢 WhatsApp de Administración Conectado</strong>
                <hr className="my-2" />
                <p className="mb-2">
                  ✅ Este WhatsApp está configurado para enviar notificaciones
                  automáticas a los residentes:
                </p>
                <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                  <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                  <li>📦 Registro de correspondencia</li>
                  <li>📢 Mensajes masivos de cartelera</li>
                </ul>
                <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                  <strong>🔒 Seguridad:</strong> Este WhatsApp debe mantenerse
                  exclusivo para administración.
                </p>
              </div>
            </Alert>
          </div>
        )}

        {/* STATE 2: WhatsApp not connected */}
        {!isConnected && (
          <div>
            {/* STATE 2a: User stopped polling or auto-stopped - Show generate QR button */}
            {userStoppedPolling && (
              <div className="text-center">
                <Alert variant="warning" className="mb-3">
                  <i className="mdi mdi-information me-2"></i>
                  WhatsApp no está conectado. Genera un código QR para conectar.
                </Alert>
                <Button
                  variant="primary"
                  onClick={fetchQRCode}
                  disabled={qrLoading}
                  size="lg"
                >
                  {qrLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generando QR...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-qrcode me-2"></i>
                      Generar Código QR
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* STATE 2b: Loading initial setup */}
            {!userStoppedPolling &&
              (loading || (!qrCode && !autoCreationAttempted)) && (
                <div>
                  <div className="mb-3">
                    <SpinnerCitoPlus text="" />
                    <span>Configurando WhatsApp automáticamente...</span>
                  </div>
                  <Alert variant="info" className="text-start">
                    <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                      <strong>
                        📢 Configurando WhatsApp de Administración
                      </strong>
                      <hr className="my-2" />
                      <p className="mb-2">
                        Se está preparando la conexión para enviar
                        notificaciones automáticas:
                      </p>
                      <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                        <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                        <li>📦 Registro de correspondencia</li>
                        <li>📢 Mensajes masivos de cartelera</li>
                      </ul>
                      <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                        <strong>🔒 Importante:</strong> Solo usar número de
                        administración.
                      </p>
                    </div>
                  </Alert>
                </div>
              )}

            {/* STATE 2c: QR Code available and polling active */}
            {!userStoppedPolling && qrCode && (
              <div>
                <Alert variant="warning" className="text-start mb-3">
                  <div style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                    <strong>
                      📢 Importante – Conexión de WhatsApp en CitoPlus
                    </strong>
                    <hr className="my-2" />
                    <p className="mb-2">
                      El siguiente código QR es para conectar el WhatsApp del
                      celular de la administración 📱.
                    </p>
                    <p className="mb-2">
                      <strong>Se enviarán notificaciones de:</strong>
                    </p>
                    <ul className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                      <li>🚶‍♂️ Ingreso y salida de visitantes</li>
                      <li>📦 Registro de correspondencia</li>
                      <li>📢 Mensajes masivos de cartelera</li>
                    </ul>
                    <p className="mb-0" style={{ fontSize: "0.8rem" }}>
                      <strong>
                        ⚠️ NO conectar números de portería ni guardas.
                      </strong>
                      <br />
                      <strong>
                        🔒 Usar únicamente número de administración o SIM
                        exclusiva.
                      </strong>
                    </p>
                  </div>
                </Alert>
                <div className="mb-3">
                  {qrLoading ? (
                    <div
                      className="d-flex align-items-center justify-content-center border rounded"
                      style={{
                        width: "200px",
                        height: "200px",
                        margin: "0 auto",
                      }}
                    >
                      <Spinner animation="border" />
                    </div>
                  ) : (
                    <img
                      src={qrCode}
                      alt="WhatsApp QR Code"
                      className="img-fluid border rounded p-2"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        margin: "0 auto",
                        display: "block",
                      }}
                    />
                  )}
                </div>
                <div className="mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={fetchQRCode}
                    disabled={qrLoading}
                    className="me-2"
                  >
                    <i className="mdi mdi-refresh me-1"></i>
                    Generar Nuevo QR
                  </Button>
                  {polling && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={stopStatusPolling}
                    >
                      <i className="mdi mdi-stop me-1"></i>
                      Detener verificación
                    </Button>
                  )}
                </div>
                {polling && (
                  <div>
                    <div className="text-center mb-3">
                      <small className="text-success">
                        <Spinner size="sm" className="me-1" />
                        Verificando conexión automáticamente...
                      </small>
                    </div>
                    {countdown && (
                      <CountdownTimer
                        remainingSeconds={countdown.remainingSeconds}
                        totalMinutes={3}
                        variant="primary"
                        showProgressBar={true}
                        size="sm"
                        className="mt-2"
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppQRDashboard;
